// src/components/EventsSection.jsx
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import styles from "../styles/FixturePage.module.css";

const normalizeEventType = (value) => String(value || "").trim().toLowerCase();
const isGoalEvent = (event) => normalizeEventType(event?.type) === "goal";

const eventIcon = (event) => {
  const t = normalizeEventType(event?.type);
  if (t === "goal") return "●";
  if (t === "card") return "■";
  if (t === "subst" || t === "substitution") return "↕";
  return "•";
};

const formatMinute = (event) => {
  const elapsed = Number(event?.elapsed);
  const extra = Number(event?.extra);
  if (Number.isFinite(elapsed) && Number.isFinite(extra) && extra > 0) return `${elapsed}+${extra}'`;
  if (Number.isFinite(elapsed)) return `${elapsed}'`;
  return "—";
};

const sortEventsAsc = (rows = []) =>
  [...rows].sort((a, b) => {
    const ae = Number(a?.elapsed); const be = Number(b?.elapsed);
    const ax = Number(a?.extra); const bx = Number(b?.extra);
    const aElapsed = Number.isFinite(ae) ? ae : -1;
    const bElapsed = Number.isFinite(be) ? be : -1;
    if (aElapsed !== bElapsed) return aElapsed - bElapsed;
    const aExtra = Number.isFinite(ax) ? ax : 0;
    const bExtra = Number.isFinite(bx) ? bx : 0;
    return aExtra - bExtra;
  });

const normalizeEvents = (rawEvents = []) =>
  (Array.isArray(rawEvents) ? rawEvents : [])
    .map((e, idx) => ({
      id: e?.id ?? `${e?.type || "ev"}-${e?.elapsed ?? "x"}-${idx}`,
      elapsed: e?.elapsed ?? e?.time?.elapsed ?? null,
      extra: e?.extra ?? e?.time?.extra ?? null,
      team_id: e?.team_id ?? e?.team?.id ?? null,
      team_name: e?.team_name ?? e?.team?.name ?? "Unknown",
      type: e?.type ?? "Event",
      detail: e?.detail ?? "",
      comments: e?.comments ?? "",
      player_name: e?.player_name ?? e?.player?.name ?? "",
      assist_name: e?.assist_name ?? e?.assist?.name ?? "",
    }))
    .filter((e) => e.type || e.player_name || e.team_name);

const EventsSection = ({ fixtureId, initialEvents = null, homeTeamId = null, awayTeamId = null, title = "Match Events" }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const initial = normalizeEvents(initialEvents || []);
    if (initial.length) {
      setEvents(sortEventsAsc(initial));
      return;
    }
    if (!fixtureId) return;

    let cancelled = false;
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const res = await api.get("/football/events", { params: { fixture_id: fixtureId } });
        if (cancelled) return;
        const normalized = normalizeEvents(res.data?.response || res.data?.events || []);
        setEvents(sortEventsAsc(normalized));
      } catch (err) {
        console.error("Error fetching events:", err);
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchEvents();
    return () => {
      cancelled = true;
    };
  }, [fixtureId, initialEvents]);

  const rows = useMemo(() => sortEventsAsc(events), [events]);

  if (loading && !rows.length) return <div className={styles.matchEmpty}>Loading match events...</div>;
  if (!rows.length) return <div className={styles.matchEmpty}>No match events available yet.</div>;

  return (
    <section className={styles.timelinePanel}>
      <div className={styles.sectionTitleRow}>
        <div>
          <span className={styles.sectionEyebrow}>Match centre</span>
          <h2>{title === "Match Events" ? "Timeline" : title}</h2>
        </div>
        <span className={styles.timelineCount}>{rows.length} events</span>
      </div>
      <div className={styles.timelineDivider}>
        <span>Match timeline</span>
      </div>
      <div className={styles.timelineList}>
        {rows.map((e) => {
          const teamSide =
            homeTeamId && awayTeamId
              ? Number(e.team_id) === Number(homeTeamId)
                ? "home"
                : Number(e.team_id) === Number(awayTeamId)
                  ? "away"
                  : ""
              : "";
          const type = normalizeEventType(e.type);
          return (
            <div
              key={e.id}
              className={`${styles.timelineEvent} ${
                teamSide === "away" ? styles.timelineEventAway : styles.timelineEventHome
              }`}
            >
              <div className={styles.timelineContent}>
                <span className={`${styles.eventGlyph} ${type === "card" ? styles.cardGlyph : ""}`}>
                  {eventIcon(e)}
                </span>
                <div>
                  <strong>{e.player_name || e.team_name || "Match event"}</strong>
                  <span>
                    {isGoalEvent(e) && e.assist_name
                      ? `Assist: ${e.assist_name}`
                      : e.detail || e.comments || e.team_name}
                  </span>
                </div>
              </div>
              <time>{formatMinute(e)}</time>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default EventsSection;
